import React, { useEffect, useState } from "react";
import {
  Autocomplete, Box, Button, Chip, Dialog, DialogTitle, Divider, Grid,
  IconButton, InputAdornment, TextField, Tooltip, Typography, CircularProgress,
} from "@mui/material";
import { Add, Close, Delete, Edit, Search, SecurityRounded, BusinessRounded, AccountTreeOutlined, MarkEmailUnread } from "@mui/icons-material";
import CustomSnackbar from "../components/Common/CustomSnackbar";
import { useSnackbar } from "../components/Common/hooks/useSnackbar";
import useAuth from "../hoc/AuthProvider/useAuth";
import { Permissions } from "../config/permissions";
import { getUserList, createUser, updateUser, deleteUser, resendInvitation } from "../services/userService";
import { getRoleList } from "../services/roleService";
import { getCompanyList } from "../services/companyService";
import { getDepartmentList } from "../services/departmentService";

interface RoleItem       { id: number; name: string }
interface CompanyItem    { id: number; name: string }
interface DepartmentItem { id: number; name: string }

interface UserDto {
  userId: number;
  userName: string;
  userEmail: string;
  phone: string;
  isActive: boolean;
  isPasswordChange: boolean;
  roles: RoleItem[];
  companies: CompanyItem[];
  departments: DepartmentItem[];
}

const emptyForm = {
  userName:      "",
  userEmail:     "",
  phone:         "",
  roleIds:       [] as number[],
  companyIds:    [] as number[],
  departmentIds: [] as number[],
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^(\+90|0)?5\d{9}$/;

const validateForm = (form: typeof emptyForm, _isCreate: boolean) => {
  const errs: Partial<Record<keyof typeof emptyForm, string>> = {};
  if (!form.userName.trim())                                          errs.userName      = "Kullanıcı adı boş olamaz.";
  if (!form.userEmail.trim())                                         errs.userEmail     = "E-posta boş olamaz.";
  else if (!EMAIL_REGEX.test(form.userEmail))                         errs.userEmail     = "Geçersiz e-posta formatı.";
  if (form.phone && !PHONE_REGEX.test(form.phone.replace(/[\s-]/g, ""))) errs.phone    = "Geçersiz telefon numarası. Örnek: 05xx1234567";
  if (form.companyIds.length === 0)                                   errs.companyIds    = "En az bir şirket seçiniz.";
  if (form.departmentIds.length === 0)                                errs.departmentIds = "En az bir departman seçiniz.";
  return errs;
};

const Users: React.FC = () => {
  const { havePermission } = useAuth();
  const { showSuccess, showError, snackbarProps } = useSnackbar();

  const [users,       setUsers]       = useState<UserDto[]>([]);
  const [roles,       setRoles]       = useState<RoleItem[]>([]);
  const [companies,   setCompanies]   = useState<CompanyItem[]>([]);
  const [departments, setDepartments] = useState<DepartmentItem[]>([]);
  const [loading,     setLoading]     = useState(false);
  const [search,      setSearch]      = useState("");
  const [dialogOpen,  setDialogOpen]  = useState(false);
  const [editId,      setEditId]      = useState<number | null>(null);
  const [form,        setForm]        = useState(emptyForm);
  const [errors,      setErrors]      = useState<Partial<Record<keyof typeof emptyForm, string>>>({});

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes, companiesRes, departmentsRes] = await Promise.all([
        getUserList(), getRoleList(), getCompanyList(), getDepartmentList(),
      ]);
      setUsers((usersRes.data as any).data       ?? []);
      setRoles((rolesRes.data as any).data       ?? []);
      setCompanies((companiesRes.data as any).data   ?? []);
      setDepartments((departmentsRes.data as any).data ?? []);
    } catch (e) {
      showError(e as Error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setErrors({});
    setDialogOpen(true);
  };

  const openEdit = (user: UserDto) => {
    setEditId(user.userId);
    setForm({
      userName:      user.userName,
      userEmail:     user.userEmail,
      phone:         user.phone ?? "",
      roleIds:       user.roles.map(r => r.id),
      companyIds:    user.companies.map(c => c.id),
      departmentIds: user.departments.map(d => d.id),
    });
    setErrors({});
    setDialogOpen(true);
  };

  const handleClose = () => { setDialogOpen(false); setErrors({}); };

  const handleSave = async () => {
    const errs = validateForm(form, editId === null);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    try {
      if (editId !== null) {
        await updateUser({ userId: editId, ...form });
        showSuccess("Kullanıcı güncellendi.");
      } else {
        await createUser(form);
        showSuccess("Kullanıcı oluşturuldu.");
      }
      setDialogOpen(false);
      await fetchData();
    } catch (e) {
      showError(e as Error);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteUser(id);
      showSuccess("Kullanıcı silindi.");
      await fetchData();
    } catch (e) {
      showError(e as Error);
    }
  };

  const handleResendInvitation = async (userId: number) => {
    try {
      await resendInvitation(userId);
      showSuccess("Aktivasyon maili yeniden gönderildi.");
    } catch (e) {
      showError(e as Error);
    }
  };

  const filtered = users.filter(
    u => u.userName.toLowerCase().includes(search.toLowerCase()) ||
         u.userEmail.toLowerCase().includes(search.toLowerCase())
  );

  const selectedRoles       = roles.filter(r => form.roleIds.includes(r.id));
  const selectedCompanies   = companies.filter(c => form.companyIds.includes(c.id));
  const selectedDepartments = departments.filter(d => form.departmentIds.includes(d.id));

  return (
    <>
      <div className="panel-container">
        <Box sx={{ bgcolor: "#fff", p: 2, boxShadow: "0 4px 8px rgba(0,0,0,0.1)", mb: 2 }}>
          <Grid container spacing={2} alignItems="center" justifyContent="space-between">
            <Grid size={{ xs: 12, sm: "auto" }}>
              <TextField size="small" placeholder="Kullanıcı ara" value={search}
                onChange={e => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
            </Grid>
            {havePermission(Permissions.User_Create) && (
              <Grid size={{ xs: 12, sm: "auto" }}>
                <Button variant="contained" startIcon={<Add />} onClick={openCreate}
                  sx={{ bgcolor: "var(--color-primary)", "&:hover": { bgcolor: "var(--color-primary-dark)" }, textTransform: "none", borderRadius: "6px" }}>
                  Kullanıcı Ekle
                </Button>
              </Grid>
            )}
          </Grid>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
        ) : (
          <Box sx={{ bgcolor: "#fff", boxShadow: "0 4px 8px rgba(0,0,0,0.1)", borderRadius: 1 }}>
            {filtered.map((user, idx) => (
              <Box key={user.userId} sx={{
                display: "flex", alignItems: "center", p: 2,
                borderBottom: idx < filtered.length - 1 ? "1px solid #f0f0f0" : "none", gap: 2,
              }}>
                <Box sx={{ flex: 1 }}>
                  <Typography fontWeight={600}>{user.userName}</Typography>
                  <Typography variant="body2" color="text.secondary">{user.userEmail}</Typography>
                  <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
                    {user.roles.map(r => (
                      <Tooltip key={r.id} title="Rol" arrow placement="top">
                        <Chip label={r.name} size="small"
                          icon={<SecurityRounded sx={{ fontSize: "13px !important" }} />}
                          sx={{ bgcolor: "#f3f0ff", color: "#6941c6", border: "1px solid #d9d0ff", "& .MuiChip-icon": { color: "#6941c6" } }} />
                      </Tooltip>
                    ))}
                    {user.companies.map(c => (
                      <Tooltip key={`c-${c.id}`} title="Şirket" arrow placement="top">
                        <Chip label={c.name} size="small"
                          icon={<BusinessRounded sx={{ fontSize: "13px !important" }} />}
                          sx={{ bgcolor: "#eff8ff", color: "#1570ef", border: "1px solid #b2ddff", "& .MuiChip-icon": { color: "#1570ef" } }} />
                      </Tooltip>
                    ))}
                    {user.departments.map(d => (
                      <Tooltip key={`d-${d.id}`} title="Departman" arrow placement="top">
                        <Chip label={d.name} size="small"
                          icon={<AccountTreeOutlined sx={{ fontSize: "13px !important" }} />}
                          sx={{ bgcolor: "#ecfdf3", color: "#027a48", border: "1px solid #a9efc5", "& .MuiChip-icon": { color: "#027a48" } }} />
                      </Tooltip>
                    ))}
                  </Box>
                </Box>
                {havePermission(Permissions.User_Create) && !user.isPasswordChange && (
                  <Tooltip title="Aktivasyon mailini yeniden gönder" arrow>
                    <IconButton size="small" color="warning" onClick={() => handleResendInvitation(user.userId)}>
                      <MarkEmailUnread fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {havePermission(Permissions.User_Update) && user.userId !== 1 && (
                  <IconButton size="small" onClick={() => openEdit(user)}><Edit fontSize="small" /></IconButton>
                )}
                {havePermission(Permissions.User_Delete) && user.userId !== 1 && (
                  <IconButton size="small" color="error" onClick={() => handleDelete(user.userId)}><Delete fontSize="small" /></IconButton>
                )}
              </Box>
            ))}
            {filtered.length === 0 && (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography color="text.secondary">Kayıt bulunamadı.</Typography>
              </Box>
            )}
          </Box>
        )}
      </div>

      <Dialog open={dialogOpen} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Typography fontWeight={600} fontSize={18}>{editId !== null ? "Kullanıcı Düzenle" : "Kullanıcı Ekle"}</Typography>
          <IconButton onClick={handleClose} size="small"><Close fontSize="small" /></IconButton>
        </DialogTitle>
        <Divider />
        <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
          {/* Chrome autocomplete trap */}
          <input type="text" style={{ display: "none" }} readOnly />
          <input type="password" style={{ display: "none" }} readOnly />

          <TextField size="small" label="Kullanıcı Adı *" value={form.userName} fullWidth
            autoComplete="off"
            onChange={e => { setForm({ ...form, userName: e.target.value }); setErrors(p => ({ ...p, userName: undefined })); }}
            error={!!errors.userName} helperText={errors.userName} />

          <TextField size="small" label="E-posta *" value={form.userEmail} fullWidth
            autoComplete="off"
            onChange={e => { setForm({ ...form, userEmail: e.target.value }); setErrors(p => ({ ...p, userEmail: undefined })); }}
            error={!!errors.userEmail} helperText={errors.userEmail} />

          <TextField size="small" label="Telefon" value={form.phone} fullWidth
            autoComplete="off"
            onChange={e => { setForm({ ...form, phone: e.target.value }); setErrors(p => ({ ...p, phone: undefined })); }}
            error={!!errors.phone} helperText={errors.phone ?? "Örnek: 05xx1234567"} />

          <Autocomplete multiple size="small" options={companies} value={selectedCompanies}
            getOptionLabel={o => o.name} isOptionEqualToValue={(o, v) => o.id === v.id}
            onChange={(_, v) => { setForm({ ...form, companyIds: v.map(c => c.id) }); setErrors(p => ({ ...p, companyIds: undefined })); }}
            renderInput={params => <TextField {...params} label="Şirketler *" placeholder="Şirket seçiniz"
              error={!!errors.companyIds} helperText={errors.companyIds} />} />

          <Autocomplete multiple size="small" options={departments} value={selectedDepartments}
            getOptionLabel={o => o.name} isOptionEqualToValue={(o, v) => o.id === v.id}
            onChange={(_, v) => { setForm({ ...form, departmentIds: v.map(d => d.id) }); setErrors(p => ({ ...p, departmentIds: undefined })); }}
            renderInput={params => <TextField {...params} label="Departmanlar *" placeholder="Departman seçiniz"
              error={!!errors.departmentIds} helperText={errors.departmentIds} />} />

          <Autocomplete multiple size="small" options={roles} value={selectedRoles}
            getOptionLabel={o => o.name} isOptionEqualToValue={(o, v) => o.id === v.id}
            onChange={(_, v) => setForm({ ...form, roleIds: v.map(r => r.id) })}
            renderInput={params => <TextField {...params} label="Roller" placeholder="Rol seçiniz" />} />

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 1 }}>
            <Button variant="outlined" onClick={handleClose}
              sx={{ textTransform: "none", borderRadius: "6px", borderColor: "var(--color-primary)", color: "var(--color-primary)" }}>
              İptal
            </Button>
            <Button variant="contained" onClick={handleSave}
              sx={{ textTransform: "none", borderRadius: "6px", bgcolor: "var(--color-primary)", "&:hover": { bgcolor: "var(--color-primary-dark)" } }}>
              Kaydet
            </Button>
          </Box>
        </Box>
      </Dialog>

      <CustomSnackbar {...snackbarProps} />
    </>
  );
};

export default Users;
